/* eslint-disable no-nested-ternary */
import React, {
  memo, useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { StyledTh } from '../styles';
import { debounce } from '../../../../../api/utils';

function DocTableListerHeader({
  columns, onSetOrder, order, columnSizes,
  onSetColumnSizes,
}) {
  const ref = useRef();
  const sizes = useRef({});
  const onResize = useCallback(
    () => {
      const node = ref.current;
      if (node) {
        sizes.current = [...node.querySelectorAll('th[data-col]:not(:last-child)')].reduce(
          (R, th) => ({
            ...R,
            [th.dataset.col]: `${th.getBoundingClientRect().width}px`,
          }),
          {},
        );
        onSetColumnSizes(sizes.current);
      }
    },
    [onSetColumnSizes],
  );
  const observer = useMemo((() => new ResizeObserver(debounce(onResize))), [onResize]);
  useEffect(
    // eslint-disable-next-line consistent-return
    () => {
      const node = ref.current;
      if (columns && node) {
        sizes.current = columns.reduce((R, col) => ({
          ...R,
          [col.key]: columnSizes
            ? columnSizes[col.key] : null,
        }), {});
        node.querySelectorAll('[data-col]:not(:last-child)').forEach(
          (subnode) => observer.observe(subnode),
        );
        return () => {
          observer.disconnect();
        };
      }
    },
    [columnSizes, columns, observer, onSetColumnSizes],
  );
  return (
    <thead>
      <tr ref={ref}>
        <StyledTh className="icon-column">&nbsp;</StyledTh>
        {columns.map((dc) => (
          <StyledTh
            key={dc.key}
            data-col={dc.key}
            width={!(columnSizes) ? null : columnSizes[dc.key] === '1fr' ? null : columnSizes[dc.key]}
            orderable={dc.orderable}
          >
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
            jsx-a11y/no-static-element-interactions */}
            <span onClick={(e) => dc.orderable && onSetOrder(e, dc.key)}>
              {dc.label}
            </span>
            {order.column === dc.key && order.isAscending && (
            <FontAwesomeIcon icon={faAngleUp} />
            )}
            {order.column === dc.key && !order.isAscending && (
            <FontAwesomeIcon icon={faAngleDown} />
            )}
          </StyledTh>
        ))}
      </tr>
    </thead>
  );
}

DocTableListerHeader.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    orderable: PropTypes.bool,
  })).isRequired,
  onSetOrder: PropTypes.func.isRequired,
  order: PropTypes.shape({
    column: PropTypes.string,
    isAscending: PropTypes.bool,
  }).isRequired,
  columnSizes: PropTypes.shape(),
  onSetColumnSizes: PropTypes.func.isRequired,
};

DocTableListerHeader.defaultProps = {
  columnSizes: {},
};

export default memo(DocTableListerHeader);
